
import CompanyPicker from '@/components/CompanyPicker/CompanyPicker.vue'
import DataTable from '@/components/DataTable/index.vue'
import FormInput from '@/components/FormInput/FormInput.vue'
import IconAction from '@/components/IconAction/IconAction.vue'
import PageHeader from '@/components/PageHeader/PageHeader.vue'
import SelectPicker from '@/components/SelectPicker/SelectPicker.vue'
import UserPicker from '@/components/UserPicker/UserPicker.vue'
import Widget from '@/components/Widget/Widget.vue'
import ViewModel from '@/models/ViewModel'
import {
  currencyMask,
  percentagDecimaleMask,
  percentagDecimalNegativeMask,
} from '@/models/interface/Masks'
import {
  Component, Prop, Watch, Ref, Vue,
} from 'vue-property-decorator'
import FooterNav from '@/components/FooterNav/FooterNav.vue'
import DatePicker from '@/components/DatePicker/DatePicker.vue'
import MediaPackagePicker from '@/components/MediaPackagePicker/MediaPackagePicker.vue'
import FinancialPlan from '@/models/FinancialPlan'
import { RegionOptions, Products, ProductsAll } from '@/models/interface/Common'

import SearchInput from '@/components/SearchInput/SearchInput.vue'
import {
  capitalize, clone, debounce, groupBy, isEqual, keyBy, orderBy, template,
} from 'lodash'
import moment from 'moment'

import Opportunity from '@/models/Opportunity'
import Api from '@/models/Api'
import WebMessage from '@/models/WebMessage'
import OpportunityTable from '../Opportunity/components/OpportunityTable.vue'
import ForecastSort from './components/ForecastSort.vue'

Component.registerHooks(['beforeRouteLeave'])

// interface Forecast inherits FinancialPlan
type Month =
  | 'january'
  | 'february'
  | 'march'
  | 'april'
  | 'may'
  | 'june'
  | 'july'
  | 'august'
  | 'september'
  | 'october'
  | 'november'
  | 'december'

interface Forecast extends FinancialPlan {
  label: string
  months: { [K in Month]: FinancialPlan }
}

interface ForecastFilter {
  agency_id?: string | null
  advertiser_id?: string | null
  sales_rep_id?: string | null
  product: any[]
  status: string[] | []
  period: number
  quarter: number[] | []
  hide_zero: boolean
  region: string | null
}

@Component({
  components: {
    PageHeader,
    Widget,
    FormInput,
    CompanyPicker,
    SelectPicker,
    UserPicker,
    DataTable,
    IconAction,
    OpportunityTable,
    FooterNav,
    DatePicker,
    MediaPackagePicker,
    SearchInput,
    ForecastSort,
  },
})
export default class GoalPlan extends ViewModel {
  // dataTable Ref
  @Ref() readonly dataTable!: DataTable

  @Ref() readonly overviewTable!: HTMLFormElement

  @Prop()
  public year!: number

  @Prop({ default: null })
  public region!: string | null

  @Prop({ default: null })
  public agency!: string | null

  public mode: string = 'view' // view or edit

  @Watch('year')
  public onYearChange() {
    this.refresh()
  }

  @Watch('region')
  public onRegionChange() {
    this.refresh()
  }

  @Watch('agency')
  public onAgencyChange() {
    this.refresh()
  }

  public multipleQuarters: boolean = false

  public filters: ForecastFilter = {
    period: moment().year(),
    quarter: [3],
    product: ProductsAll.map(p => p.value),
    status: [],
    hide_zero: true,
    region: null,
  }

  public filter_mode: string = 'exclusive'

  public query: any[] = []

  public opportunity_records: number = 0

  public opportunity_per_page: number = 25

  public opportunity_page: number = 1

  public sort_by: string = 'name'

  public sort_desc: boolean = false

  public fieldFilters: any = {}

  public sort_forecast: any = {
    key: 'agency_id',
    sort: 'asc',
  }

  public forecast_table_height: number = 700

  @Watch('filters.quarter', { immediate: true })
  public onFiltersQuarterChange(val: any[]) {
    if (!this.ready) return
    this.loadingStates.forecasts = true
    if (!val.length) {
      const currentQuarter = moment().quarter()
      Vue.set(this.filters, 'quarter', [currentQuarter])
    }
  }

  @Watch('filters.hide_zero', { immediate: true })
  public onFiltersHideZeroChange() {
    if (!this.ready) return
    this.loadingStates.forecasts = true
  }

  @Watch('group_visibility', { deep: true })
  public onGroupVisibilityChange(val: any, oldVal: any) {
    if (!this.ready) return
    localStorage.setItem('goal_group_visibility', JSON.stringify(val))

    // check for child like related:x \ group_name:col_name
    const hasRelatedChildren = (group: string) =>
      this.group_visibility.some(g => g.startsWith(`${group}:`))

    // if user selects related and has no children, we select all related:x
    if (val.includes('related') && !hasRelatedChildren('related') && !oldVal.includes('related')) {
      this.group_visibility.push(
        'related:agency',
        'related:advertiser',
        'related:product',
        'related:sales_rep',
      )
    } else if (!hasRelatedChildren('related') && val.includes('related')) {
      this.group_visibility = this.group_visibility.filter(g => !g.startsWith('related'))
    } else if (
      !val.includes('related')
      && oldVal.includes('related')
      && hasRelatedChildren('related')
    ) {
      // if all is selected and user removed parent related, we remove all related:x
      this.group_visibility = this.group_visibility.filter(g => !g.startsWith('related'))
    } else if (
      !val.includes('related')
      && !oldVal.includes('related')
      && hasRelatedChildren('related')
    ) {
      // if related is not selected and user selects a related: child we add related
      this.group_visibility.push('related')
    }
  }

  public additional_revenue_changes: any = {}

  public plan = new FinancialPlan()

  public model = new FinancialPlan()

  public is_searching: boolean = false

  public ready: boolean = false

  public group_visibility: any = [
    'related',
    'related:agency',
    'related:advertiser',
    'related:product',
    'related:sales_rep',
    'close_won',
    'open_pipeline',
    'additional_revenue',
    'total_forecast',
    'commentary',
  ]

  public sticky_columns: string[] = []

  public fixed_header: boolean = false

  @Watch('fixed_header')
  public onFixedHeaderChange(val: boolean) {
    if (!this.ready) return
    localStorage.setItem('goal_fixed_header', JSON.stringify(val))
  }

  @Watch('sticky_columns', { deep: true })
  public onStickyColumnsChange(val: string[]) {
    if (!this.ready) return
    localStorage.setItem('goal_sticky_columns', JSON.stringify(val))
  }

  public getStickyStyle(column: string) {
    if (!this.sticky_columns.includes(column)) {
      // Return default styling for non-sticky columns
      return {
        boxShadow: '0 -1px 0 0 #dee2e6, 1px 0 0 0 #dee2e6',
      }
    }

    let left = 0
    const columnOrder = ['agency', 'advertiser', 'product', 'sales_rep']
    const columnWidths = {
      agency: 200,
      advertiser: 200,
      product: 200,
      sales_rep: 200,
    }

    // Calculate left position
    for (let i = 0; i < columnOrder.indexOf(column); i++) {
      const col = columnOrder[i]
      if (this.sticky_columns.includes(col) && this.group_visibility.includes(`related:${col}`)) {
        left += columnWidths[col as keyof typeof columnWidths]
      }
    }

    // Calculate box shadow for proper borders
    const isFirstStickyColumn = this.isFirstStickyColumn(column)
    const isLastStickyColumn = this.isLastStickyColumn(column)

    let boxShadow = 'inset 0 -1px 0 0 #dee2e6' // Bottom border (always present)

    // Add left border for first sticky column (table edge)
    if (isFirstStickyColumn) {
      boxShadow += ', inset 1px 0 0 0 #dee2e6' // Left border for table edge
    }

    // Add left border for all non-first sticky columns (column separators)
    if (!isFirstStickyColumn) {
      boxShadow += ', inset 1px 0 0 0 #dee2e6' // Left border to separate from previous column
    }

    // Add right border/shadow
    if (isLastStickyColumn) {
      // Last sticky column needs a right shadow to separate from non-sticky columns
      // Use a subtle shadow that gives visual separation without being too prominent
      boxShadow += ', 3px 0 6px -3px rgba(0,0,0,0.12), 1px 0 0 0 #dee2e6'
    } else {
      // Non-last sticky columns get a regular right border
      boxShadow += ', 1px 0 0 0 #dee2e6'
    }

    return {
      position: 'sticky',
      left: `${left}px`,
      zIndex: 10,
      minWidth: `${columnWidths[column as keyof typeof columnWidths]}px`,
      maxWidth: `${columnWidths[column as keyof typeof columnWidths]}px`,
      boxShadow,
    }
  }

  public toggleMode() {
    this.loadingStates.forecasts = true

    setTimeout(() => {
      this.mode = this.mode === 'view' ? 'edit' : 'view'
      this.loadingStates.forecasts = false
    }, 200)
  }

  public isFirstStickyColumn(column: string): boolean {
    const columnOrder = ['agency', 'advertiser', 'product', 'sales_rep']
    const visibleStickyColumns = columnOrder.filter(
      col =>
        this.sticky_columns.includes(col) && this.group_visibility.includes(`related:${col}`),
    )
    return visibleStickyColumns[0] === column
  }

  public isLastStickyColumn(column: string): boolean {
    const columnOrder = ['agency', 'advertiser', 'product', 'sales_rep']
    const visibleStickyColumns = columnOrder.filter(
      col =>
        this.sticky_columns.includes(col) && this.group_visibility.includes(`related:${col}`),
    )
    return visibleStickyColumns[visibleStickyColumns.length - 1] === column
  }

  public getStickyHeaderStyle() {
    const hasAnySticky = this.sticky_columns.some(col =>
      ['agency', 'advertiser', 'product', 'sales_rep'].includes(col))

    if (!hasAnySticky) return {}

    // Calculate the total width of ALL visible related columns (both sticky and non-sticky)
    // when the header needs to be sticky
    let totalWidth = 0
    const columnOrder = ['agency', 'advertiser', 'product', 'sales_rep']
    const columnWidths = {
      agency: 200,
      advertiser: 200,
      product: 200,
      sales_rep: 200,
    }

    columnOrder.forEach(col => {
      // Count all visible related columns since the header spans all of them
      if (this.group_visibility.includes(`related:${col}`)) {
        totalWidth += columnWidths[col as keyof typeof columnWidths]
      }
    })

    return {
      position: 'sticky',
      left: '0px',
      zIndex: 11,
      backgroundColor: '#fff',
      borderRight: '2px solid #dee2e6',
      width: `${totalWidth}px`,
      minWidth: `${totalWidth}px`,
      maxWidth: `${totalWidth}px`,
    }
  }

  // Total Number of records
  public records: number = 0

  public loadingStates: { [key: string]: boolean } = {
    forecasts: false,
    opportunities: false,
  }

  public query_settings: any = {
    company_fields: [
      {
        name: 'company',
        key: 'company_id',
      },
      {
        name: 'agency',
        key: 'agency_id',
      },
    ],
    user_fields: [
      {
        name: 'sr',
        key: 'sales_rep_id',
      },
    ],
  }

  public advanced_checksum: string = ''

  public advanced_delete: string[] = []

  public get isLoading() {
    return this.loadingStates.forecasts || this.loadingStates.opportunities
  }

  public get all_product_selected() {
    return this.filters.product?.length && this.filters.product.length === ProductsAll.length
  }

  public get options() {
    return {
      regions: RegionOptions,
      products: [...ProductsAll],
      opportunity_status: Opportunity.opportunity_status_text.filter(
        (f: any) => f.value !== 'lost',
      ),
      quarters: [
        { text: 'Q1', name: 'Q1', value: 1 },
        { text: 'Q2', name: 'Q2', value: 2 },
        { text: 'Q3', name: 'Q3', value: 3 },
        { text: 'Q4', name: 'Q4', value: 4 },
      ],
      group_visibility: [
        { text: 'Related', value: 'related' },
        { text: 'Closed Won', value: 'close_won' },
        { text: 'Open Pipeline', value: 'open_pipeline' },
        { text: 'Additional Revenue', value: 'additional_revenue' },
        { text: 'Total Forecast', value: 'total_forecast' },
        { text: 'Commentary', value: 'commentary' },
        { text: 'Agency', value: 'related:agency' },
        { text: 'Advertiser', value: 'related:advertiser' },
        { text: 'Product', value: 'related:product' },
        { text: 'Sales Rep', value: 'related:sales_rep' },
      ],
    }
  }

  public get masks() {
    return {
      currency: currencyMask,
      percentage: percentagDecimaleMask,
    }
  }

  public refresh() {
    this.dataTable.refresh()
  }

  public cancel() {
    this.$router.push({ name: 'GoalHome' })
  }

  public save() {
    // this.loadingStates.saving = true
    const tosave = []
    for (let key in this.additional_revenue_changes) {
      const keys = Object.keys(this.additional_revenue_changes[key])

      let obj: any = {
        id: key,
      }

      keys.forEach(k => {
        obj[k] = this.additional_revenue_changes[key][k]
      })

      tosave.push({
        ...obj,
        id: key,
      })

      obj = {}
    }

    if (!tosave.length) {
      WebMessage.warning('No changes to save')
      return Promise.resolve()
    }

    let api = new Api()
    this.loadingStates.forecasts = true
    return api
      .post('financial_plans/batch', { financial_plans: tosave })
      .then(() => {
        this.additional_revenue_changes = {}
        WebMessage.success('Records saved!')
        this.loadingStates.forecasts = false
      })
      .catch(() => {
        WebMessage.error('There was an error saving the records, please try again.')
        this.loadingStates.forecasts = false
      })
  }

  public forecast_table_data: any[] = []

  public get filteredForecastData(): Forecast[] {
    let data = this.forecast_table_data.filter(item => {
      if (this.filters.agency_id && item.agency_id !== this.filters.agency_id) {
        return false
      }
      if (this.filters.advertiser_id && item.advertiser_id !== this.filters.advertiser_id) {
        return false
      }
      if (this.filters.sales_rep_id && item.sales_rep_id !== this.filters.sales_rep_id) {
        return false
      }

      if (this.filters.region && item.region !== this.filters.region) {
        return false
      }

      if (!this.filters.product.includes(item.product) && !this.all_product_selected) {
        return false
      }

      return this.filters.hide_zero ? !this.hideIfMonthsAreZero(item, this.filters.hide_zero) : true
    })

    if (this.ready && this.filters.quarter) {
      this.loadingStates.forecasts = false
    }

    return data.length ? orderBy(data, [this.sort_forecast.key], [this.sort_forecast.sort]) : []
  }

  public quarterMonths: { [key: number]: string[] } = {
    1: ['january', 'february', 'march'],
    2: ['april', 'may', 'june'],
    3: ['july', 'august', 'september'],
    4: ['october', 'november', 'december'],
  }

  // get array of months from quarter number filters.quarter use momentjs
  public get monthsInQuarter() {
    return this.quarterMonths[this.filters.quarter[0]]
  }

  public get opportunity_summary_fields() {
    return [
      {
        key: 'name',
        label: 'Opportunity Name',
        class: 'text-capitalize',
        sortable: true,
      },
      {
        key: 'status',
        label: 'Status',
        class: 'text-center text-capitalize',
        type: 'badge',
        color: (opp: Opportunity) => opp.status_color,
        formatter: (value: string) => value.replace(/_/g, ' ').toLowerCase(),
        sortable: true,
      },
      {
        key: 'types',
        label: 'Product',
        class: 'text-center text-uppercase',
        sortable: true,
      },
      {
        key: 'region',
        label: 'Region',
        class: 'text-center',
        sortable: true,
      },
      {
        key: 'agency_id',
        label: 'Agency',
        class: 'text-center',
        type: 'object',
        sortable: true,
        object: {
          key: 'agency',
          thClass: 'align-middle text-center',
          property: 'name',
        },
      },
      {
        key: 'sales_rep_id',
        label: 'Sales Rep',
        class: 'text-center',
        type: 'object',
        sortable: true,
        object: {
          key: 'sales_rep',
          thClass: 'align-middle text-center',
          property: 'name',
        },
      },
      {
        key: 'advertiser_id',
        label: 'Advertiser',
        class: 'text-center',
        type: 'object',
        sortable: true,
        object: {
          key: 'advertiser',
          thClass: 'align-middle text-center',
          property: 'name',
        },
      },
      {
        key: 'station_id',
        label: 'Station',
        class: 'text-center',
        type: 'object',
        sortable: true,
        object: {
          key: 'station',
          thClass: 'align-middle text-center',
          property: 'name',
        },
      },
      ...this.monthsInQuarter.map(month => ({
        key: `${month}`,
        label: `${month.charAt(0).toUpperCase() + month.slice(1)} Revenue`,
        class: 'text-center',
        sortable: true,
      })),
    ]
  }

  public getTotalByGroup(group: string, month: string): number {
    return this.filteredForecastData.reduce((total, item: any) => {
      const monthData = item.months[month][group]

      return total + (monthData || 0)
    }, 0)
  }

  public getTotalAdditionalRevenue(month: string): number {
    return this.filteredForecastData.reduce((total, item: any) => {
      const monthData = item.months[month].goal_forecast
      return total + (monthData || 0)
    }, 0)
  }

  // Total Forecast Total Col
  // close won + aditional
  public getTotalForecastTotal(month: string): number {
    return this.filteredForecastData.reduce((total, item: any) => {
      const monthData = item.months[month]
      return total + (monthData.goal_sold || 0) + (monthData.goal_forecast || 0)
    }, 0)
  }

  public months = moment.months()

  public beforeRouteLeave(to: object, from: object, next: any) {
    if (Object.keys(this.additional_revenue_changes).length > 0) {
      WebMessage.confirm(
        'You have unsaved changes, do you want to reset these changes and discard all modifications? This action can not be undone.',
        'Unsaved changes',
        { okTitle: 'Save Changes', cancelTitle: 'Discard Changes' },
      ).then((value: boolean) => {
        if (value) {
          this.save().then(() => {
            next()
          })
        } else {
          next()
        }
      })
    } else {
      next()
    }
  }

  public loadForecastData() {
    if (Object.keys(this.additional_revenue_changes).length > 0) {
      WebMessage.confirm(
        'You have unsaved changes, do you want to reset these changes and discard all modifications? This action can not be undone.',
        'Unsaved changes',
        { okTitle: 'Save Changes', cancelTitle: 'Discard Changes' },
      ).then((value: boolean) => {
        if (value) {
          this.save().then(() => {
            this.fetchData()
          })
        } else {
          this.fetchData()
        }
      })
      return
    }

    this.fetchData()
  }

  public fetchData() {
    this.loadingStates.forecasts = true
    FinancialPlan.goalForecast(this.filters.period)
      .then(response => {
        // this.forecast_table_data = response.data.result

        this.forecast_table_data = response.data.result.map((item: any) => {
          item.local_id = this.randomUUID()

          // set defaults
          item.totals = {
            1: {
              close_won: 0,
              open_pipeline: 0,
              additional_revenue: 0,
              total_forecast: 0,
            },
            2: {
              close_won: 0,
              open_pipeline: 0,
              additional_revenue: 0,
              total_forecast: 0,
            },
            3: {
              close_won: 0,
              open_pipeline: 0,
              additional_revenue: 0,
              total_forecast: 0,
            },
            4: {
              close_won: 0,
              open_pipeline: 0,
              additional_revenue: 0,
              total_forecast: 0,
            },
          }

          // this is for Total Forecast, it uses a addition of goal_sold and goal_forecast, its dynamic and not static
          // so thats why we need to set it here, so we can use sort also on them
          item.total_forecast_months = {
            january: 0,
            february: 0,
            march: 0,
            april: 0,
            may: 0,
            june: 0,
            july: 0,
            august: 0,
            september: 0,
            october: 0,
            november: 0,
            december: 0,
          }

          for (let i = 1; i <= 4; i++) {
            const months = this.quarterMonths[i]
            item.totals[i].close_won = months.reduce(
              (acc: number, month: string) => acc + item.months[month].goal_sold,
              0,
            )
            item.totals[i].open_pipeline = months.reduce(
              (acc: number, month: string) => acc + item.months[month].goal_planned,
              0,
            )

            item.totals[i].additional_revenue = months.reduce(
              (acc: number, month: string) => acc + item.months[month].goal_forecast,
              0,
            )

            item.totals[i].total_forecast = months.reduce(
              (acc: number, month: string) =>
                acc + (item.months[month].goal_sold + item.months[month].goal_forecast),
              0,
            )

            // total_forecast_months
            months.forEach((month: string) => {
              item.total_forecast_months[month] = (item.months[month].goal_sold || 0) + (item.months[month].goal_forecast || 0)
            })
          }

          item.originals = JSON.parse(JSON.stringify(item.months))

          return item
        })
      })
      .catch(e => {
        /* eslint-disable no-console */
        console.error(e)
        this.forecast_table_data = []
        this.additional_revenue_changes = {}
      })
      .finally(() => {
        this.ready = true
        this.loadingStates.forecasts = false
      })
  }

  public calculateDynamicTotals(id: string) {
    const months = this.monthsInQuarter
    const idx = this.forecast_table_data.findIndex(item => item.local_id === id)

    // Calculate additional_revenue
    const additional_revenue = months.reduce(
      (acc: number, month: string) =>
        acc + (this.forecast_table_data[idx].months[month].goal_forecast || 0),
      0,
    )

    Vue.set(
      this.forecast_table_data[idx].totals[this.filters.quarter[0]],
      'additional_revenue',
      additional_revenue,
    )

    // Calculate total_forecast
    const total_forecast = months.reduce(
      (acc: number, month: string) =>
        acc
        + (this.forecast_table_data[idx].months[month].goal_sold || 0)
        + (this.forecast_table_data[idx].months[month].goal_forecast || 0),
      0,
    )
    Vue.set(
      this.forecast_table_data[idx].totals[this.filters.quarter[0]],
      'total_forecast',
      total_forecast,
    )

    // update total_forecast_months
    months.forEach((month: string) => {
      this.forecast_table_data[idx].total_forecast_months[month] = (this.forecast_table_data[idx].months[month].goal_sold || 0)
        + (this.forecast_table_data[idx].months[month].goal_forecast || 0)
    })
  }

  public created() {
    if (this.year) {
      this.filters.period = this.year
    }
    if (
      ['sales', 'inside_sales'].includes(this.user.type)
      && !this.user?.canOptions('management_dashboard', 'full_region')
    ) {
      this.filters.sales_rep_id = this.user.id
    }

    if (this.user.region && !this.user?.canOptions('management_dashboard', 'cross_region')) {
      this.filters.region = this.user.region
    }

    // if (!this.filters.sales_rep_id && !this.filters.region) {
    //   this.filters.region = 'east'
    // }
  }

  public mounted() {
    const selected = JSON.parse(localStorage.getItem('goal_group_visibility') || '[]')
    if (selected.length) {
      this.group_visibility = selected
    }

    const stickyColumns = JSON.parse(localStorage.getItem('goal_sticky_columns') || '[]')
    if (stickyColumns.length) {
      this.sticky_columns = stickyColumns
    } else {
      this.sticky_columns = ['agency', 'advertiser']
      localStorage.setItem('goal_sticky_columns', JSON.stringify(this.sticky_columns))
    }

    const fixedHeader = localStorage.getItem('goal_fixed_header')
      ? JSON.parse(localStorage.getItem('goal_fixed_header') || 'null')
      : null
    if (fixedHeader === null) {
      this.fixed_header = true // default to true if not set
      localStorage.setItem('goal_fixed_header', JSON.stringify(this.fixed_header))
    } else {
      this.fixed_header = fixedHeader
    }

    this.loadForecastData()
  }

  public get queries() {
    let query = []
    if (this.filters.agency_id && this.filters.agency_id.length) {
      query.push(`agency_id:${this.filters.agency_id}`)
    }
    if (this.filters.advertiser_id && this.filters.advertiser_id.length) {
      query.push(`advertiser_id:${this.filters.advertiser_id}`)
    }
    if (this.filters.sales_rep_id && this.filters.sales_rep_id.length) {
      query.push(`sales_rep_id:${this.filters.sales_rep_id}`)
    }
    if (this.filters.product && this.filters.product.length && !this.all_product_selected) {
      query.push(...this.filters.product.map((s: string) => `product:${s}`))
    }
    if (this.filters.status && this.filters.status.length) {
      query.push(...this.filters.status.map((s: string) => `status:${s}`))
    }
    if (this.filters.region && this.filters.region.length) {
      query.push(`region:${this.filters.region}`)
    }

    return [
      'is_not:lost',
      `is:active_in_${
        this.filters.product && !this.all_product_selected ? `${this.filters.product}_` : ''
      }q${this.filters.quarter}_${this.filters.period}`,
      ...query,
    ]
  }

  public async opportunitiesRows(context: any) {
    this.loadingStates.opportunities = true
    this.opportunity_page = context.currentPage
    this.opportunity_per_page = context.perPage

    let query = []
    let status_in: string[] = []
    context.filter.forEach((value: String) => {
      // check if starts with 'status:'
      if (value.startsWith('status:')) {
        status_in.push(value.replace('status:', ''))
      } else {
        query.push(value)
      }
    })

    if (status_in.length) {
      query.push(`status_in:${status_in.join(',')}`)
    }

    return Opportunity.paginate({
      page_size: context.perPage,
      page: context.currentPage,
      order_by: context.sortBy,
      order: context.sortDesc ? 'desc' : 'asc',
      query,
      mode: this.filter_mode,
    })
      .then(result => {
        this.opportunity_records = result.records

        return result.data
      })
      .finally(() => {
        this.loadingStates.opportunities = false
      })
  }

  public refreshForecast() {
    this.$nextTick(() => {
      this.loadForecastData()
    })
  }

  public resetSort() {
    this.sort_forecast = {
      key: 'agency_id',
      sort: 'asc',
    }
  }

  public updatedInput(item: any, month: any, field: string, value: any) {
    if (item.originals[month][field] == value) {
      return
    }

    item.originals[month][field] = value

    if (isNaN(value) && field != 'notes') {
      value = value.replace(/[$,]/g, '')
      value = Number(value)
    }

    if (!this.additional_revenue_changes[item.months[month].id]) {
      this.additional_revenue_changes[item.months[month].id] = {}
    }

    this.additional_revenue_changes[item.months[month].id][field] = value

    if (field == 'notes') {
      return
    }
    this.calculateDynamicTotals(item.local_id)
  }

  public hideIfMonthsAreZero(item: Forecast, hideZero: boolean): boolean {
    if (!hideZero) return false
    // check if all months in the quarter are zero, for goal_sold, goal_planned, and goal_forecast
    const months = this.monthsInQuarter.map(m => item.months[m as Month])
    const allZero = months.every(
      m => m.goal_sold === 0 && m.goal_planned === 0 && m.goal_forecast === 0,
    )
    return allZero
  }

  public delayChanges(clb: any, delay: number = 1000) {
    debounce(() => {
      clb()
    }, delay)()
  }

  public resetFilters() {
    let region: string | null = null
    let sales_rep_id: string | null = null

    if (['sales', 'inside_sales'].includes(this.user.type) && this.user.id) {
      sales_rep_id = this.user.id
    }

    if (this.user.region) {
      region = this.user.region
    }

    if (!region && !sales_rep_id) {
      region = 'east'
    }

    this.filters = {
      agency_id: null,
      advertiser_id: null,
      sales_rep_id,
      product: ProductsAll.map(p => p.value),
      status: [],
      period: moment().year(),
      quarter: [moment().quarter()],
      hide_zero: false,
      region,
    }
    this.loadForecastData()
  }

  public sortForecast(key: string) {
    this.sort_forecast = {
      key,
      sort: this.sort_forecast.sort === 'asc' ? 'desc' : 'asc',
    }
  }

  public removeStickyIfColIsHidden() {
    this.sticky_columns = this.sticky_columns.filter(col =>
      this.group_visibility.includes(`related:${col}`))
  }

  public getNonStickyColumnStyle() {
    return {
      boxShadow: '0 -1px 0 0 #dee2e6, 1px 0 0 0 #dee2e6',
    }
  }
}
